import React, { useState } from "react";
import { motion } from "framer-motion";
import { MEDIA_CLIPS } from "../../lib/content";

const FEATURED_NEWS = {
  title: "As Seen On The News",
  subtitle: "Damien Doran — Mr. Green Bay featured for his work with youth & community.",
  type: "Press · News",
  duration: "0:53",
  video: "/videos/news.mp4",
  img: "/posters/news.jpg"
};

function FeaturedNewsClip() {
  const [playing, setPlaying] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="relative mb-12 overflow-hidden rounded-3xl border border-gold/20 bg-gradient-to-br from-ink-700/80 to-ink-900"
    >
      <div className="grid lg:grid-cols-12">
        <div className="relative aspect-video lg:col-span-8 lg:aspect-auto">
          <div className="relative h-full w-full bg-black">
            <video
              src={FEATURED_NEWS.video}
              poster={FEATURED_NEWS.img}
              controls={playing}
              preload="none"
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              data-testid="featured-news-video"
              className="h-full w-full object-cover"
            />
            {!playing && (
              <>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-transparent" />
                <button
                  data-testid="featured-news-play"
                  onClick={(e) => {
                    const v = e.currentTarget.parentElement.querySelector("video");
                    if (v) { v.play(); setPlaying(true); }
                  }}
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label="Play news interview"
                >
                  <span className="flex h-24 w-24 items-center justify-center rounded-full border border-gold/60 bg-black/70 text-gold backdrop-blur-md transition-transform hover:scale-110 animate-pulse-gold">
                    <i className="fa-solid fa-play text-3xl" />
                  </span>
                </button>
                <div className="pointer-events-none absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-black/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-red-400 backdrop-blur">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  Press · News
                </div>
                <div className="pointer-events-none absolute right-5 top-5 rounded-full border border-white/20 bg-black/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur">
                  {FEATURED_NEWS.duration}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-center p-8 lg:col-span-4 lg:p-10">
          <div className="mb-3 inline-flex items-center gap-2 self-start rounded-full border border-gold/30 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-gold">
            <i className="fa-solid fa-tv text-[10px]" /> Featured
          </div>
          <h3 className="font-display text-3xl font-bold leading-tight text-white md:text-4xl">
            {FEATURED_NEWS.title}
          </h3>
          <p className="mt-4 text-base text-zinc-300">
            {FEATURED_NEWS.subtitle}
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
            <i className="fa-solid fa-quote-left text-gold" />
            Mainstream coverage of the movement
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function VideoCard({ m, i }) {
  const [playing, setPlaying] = useState(false);

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: 0.05 * i, duration: 0.6 }}
      className="group relative overflow-hidden rounded-3xl border border-white/5 bg-ink-700/60"
    >
      <div className="relative aspect-video overflow-hidden bg-black">
        <video
          src={m.video}
          poster={m.img}
          controls={playing}
          preload="none"
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          data-testid={`media-video-${i}`}
          className="h-full w-full object-cover"
        />
        {!playing && (
          <>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900 via-black/30 to-transparent" />
            <button
              data-testid={`media-play-${i}`}
              className="absolute inset-0 flex items-center justify-center"
              onClick={(e) => {
                const v = e.currentTarget.parentElement.querySelector("video");
                if (v) { v.play(); setPlaying(true); }
              }}
              aria-label={`Play ${m.title}`}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 bg-black/60 text-gold backdrop-blur-md transition-transform group-hover:scale-110">
                <i className="fa-solid fa-play" />
              </span>
            </button>
            <div className="pointer-events-none absolute right-4 top-4 rounded-full border border-white/20 bg-black/60 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur">
              {m.type} · {m.duration}
            </div>
          </>
        )}
      </div>
      <div className="p-6">
        <div className="font-display text-xl text-white">{m.title}</div>
        <p className="mt-2 text-sm leading-relaxed text-zinc-400">{m.desc}</p>
        <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
          <span className="rounded-full border border-gold/30 px-3 py-1 text-gold">
            <i className="fa-solid fa-microphone-lines mr-1.5 text-[10px]" /> Live Keynote
          </span>
          <span className="rounded-full border border-white/10 px-3 py-1">Middle School</span>
        </div>
      </div>
    </motion.article>
  );
}

export default function Media() {
  return (
    <section id="media" data-testid="media-section" className="relative bg-ink-900 py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" />
              Media & Keynotes
            </div>
            <h2 className="font-display text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              Live from the room —
              <span className="block text-gradient-gold">middle school keynote.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-300">
            Real clips. Real audiences. Mr. Green Bay in front of the next generation —
            unfiltered, unscripted, and on purpose.
          </p>
        </div>

        <FeaturedNewsClip />

        <div className="grid gap-6 md:grid-cols-2">
          {MEDIA_CLIPS.map((m, i) => (
            <VideoCard key={m.title} m={m} i={i} />
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a href="https://www.youtube.com/@MrGreenBay" target="_blank" rel="noopener noreferrer" data-testid="media-link-youtube" className="btn-ghost-gold rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-wider">
            <i className="fa-brands fa-youtube mr-2" /> YouTube
          </a>
          <a href="https://www.instagram.com/mr_greenbay/" target="_blank" rel="noopener noreferrer" data-testid="media-link-instagram" className="btn-ghost-gold rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-wider">
            <i className="fa-brands fa-instagram mr-2" /> Instagram
          </a>
          <a href="https://www.tiktok.com/@mrgreenbay920" target="_blank" rel="noopener noreferrer" data-testid="media-link-tiktok" className="btn-ghost-gold rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-wider">
            <i className="fa-brands fa-tiktok mr-2" /> TikTok
          </a>
          <a href="https://www.facebook.com/Mr.GreenBay/" target="_blank" rel="noopener noreferrer" data-testid="media-link-facebook" className="btn-ghost-gold rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-wider">
            <i className="fa-brands fa-facebook-f mr-2" /> Facebook
          </a>
        </div>
      </div>
    </section>
  );
}
